<template>
  <div data-test-id="CaseTransferMediaButtonComponent">
    <div
      class="media-button btn btn-secondary border border-dark rounded d-flex align-items-center p-2"
      data-test-id="case-transfer-initiate-button"
      :disabled="caseTransfer && caseTransfer.status === 'open'"
      @click="onCaseTransferOpen"
    >
      <b-icon :icon="icon" class="m-2"></b-icon>

      <div class="d-flex flex-column flex-fill text-left ml-2">
        <strong>{{ label }}</strong>
        <span class="w-100 small font-weight-normal">Handoff case to another provider</span>
      </div>

      <b-spinner v-if="caseTransfer && caseTransfer.status !== ''" class="m-2" small />
    </div>

    <b-modal
      ref="case-transfer-modal"
      :hide-footer="!canCancelTransfer()"
      data-test-id="case-transfer-modal"
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <template #modal-header>
        <h5 class="w-100 text-center m-0">Case Number</h5>
      </template>

      <div class="text-center mb-2">
        <div>Give this number to the provider who will be receiving the case.</div>
        <h3 v-if="caseTransfer && caseTransfer.caseTransferCode" class="mt-3 mb-3" data-test-id="case-transfer-code">
          {{ caseTransfer.caseTransferCode }}
        </h3>
        <div v-if="caseTransfer && caseTransfer.request && caseTransfer.request.status === 'accepted'">
          The requester is loading the case.
        </div>
        <div v-if="caseTransfer && caseTransfer.request && caseTransfer.request.status === 'received'">
          The requester received the case.
        </div>
      </div>

      <template v-if="canCancelTransfer()" #modal-footer>
        <b-button
          id="case-transfer-cancel__button"
          data-test-id="case-transfer-cancel-button"
          variant="danger"
          block
          class="mt-2"
          @click.once="onCaseTransferCancel()"
        >
          Cancel transfer
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { BIcon } from "bootstrap-vue";
import { LoggerService as lgg } from "vue-components-library";
import PubNub from "pubnub";
import { showOkDialog } from "@/Utils/Dialogs";

const NO_INTERNET_MESSAGE =
  "You must have an active network connection to use Case Transfer. Please try again when online.";
const NO_INTERNET_WHILE_IN_TRANSFER = "Slow or no internet connection. Please check your internet settings.";

export default {
  name: "CaseTransferMediaButton",
  components: { BIcon },
  props: {
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isListening: false,
      isOnline: true,
      timeout: undefined,
      pubNubInstance: undefined,
    };
  },
  computed: {
    ...mapGetters("Case", {
      twiageCase: "twiageCase",
    }),
    ...mapGetters("PubNubSubscription", {
      pubNubSub: "pubNubSub",
    }),
    ...mapGetters("CaseTransfer", ["caseTransfer", "caseTransferType"]),
    ...mapGetters("Voip", { isVoipCallInProgress: "isCallInProgress" }),
  },
  watch: {
    caseTransfer: function (newValue, oldValue) {
      this.manageStatus(newValue, oldValue);
    },
  },
  mounted() {
    this.manageStatus(this.caseTransfer);
  },
  methods: {
    ...mapActions("Case", ["clearCase"]),
    ...mapActions("CaseTransfer", ["open", "accept", "decline", "cancel", "state", "acknowledge", "clear"]),
    ...mapActions("Global", ["checkOnlineStatus"]),
    async onCaseTransferOpen() {
      if (this.isVoipCallInProgress) {
        showOkDialog(this.$bvModal, "Warning", "Close call to start Case Transfer");
        return;
      }
      await this.clear();
      const isOnline = await this.checkOnlineStatus();
      if (isOnline) {
        try {
          await this.open(this.twiageCase.id);
        } catch (err) {
          this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
          lgg.info("Error", err);
        }
      } else {
        this.showWarningAlert("Warning", NO_INTERNET_MESSAGE);
      }
    },
    async onCaseTransferAccept() {
      try {
        await this.accept(this.caseTransfer._id);
      } catch (error) {
        this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
      }
    },
    async onCaseTransferDecline() {
      try {
        await this.decline(this.caseTransfer._id);
      } catch (error) {
        this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
      }
    },
    async onCaseTransferCancel() {
      try {
        await this.cancel(this.caseTransfer._id);
      } catch (error) {
        this.showWarningAlert("Warning", NO_INTERNET_WHILE_IN_TRANSFER);
      }
    },
    manageStatus(updatedCaseTransfer, outdatedCaseTransfer) {
      if (this.caseTransfer?.status && this.caseTransferType === "transfer") {
        if (updatedCaseTransfer?.status !== "") {
          if (updatedCaseTransfer.status === "open") {
            if (!outdatedCaseTransfer || outdatedCaseTransfer.status === "") {
              this.subscribeChannel();
              this.startInternetStatusListener();
              this.showTransferModal();
            }

            if (updatedCaseTransfer?.request?.status) {
              if (updatedCaseTransfer.request.status === "open") {
                this.showRequestAlert(
                  updatedCaseTransfer.messageForSender
                    ? updatedCaseTransfer.messageForSender
                    : "Someone wants to receive your case.",
                );
              }
              if (updatedCaseTransfer.request.status === "cancelled") {
                this.$bvModal.hide("caseTransferRequestDialog");
                this.showWarningAlert(
                  "Request Cancelled",
                  updatedCaseTransfer.messageForSender ? updatedCaseTransfer.messageForSender : "Request cancelled.",
                );
              }
              if (updatedCaseTransfer.request.status === "received") {
                this.acknowledge(this.caseTransfer._id);
              }
            }
          }
          if (updatedCaseTransfer.status === "done") {
            this.cleanUp();
            this.clearCase();
            this.showWarningAlert("Case Transfer", "The case transfer is done.");
          }
          if (updatedCaseTransfer.status === "cancelled") {
            this.cleanUp();
            this.showWarningAlert("Case Transfer", "The case transfer was cancelled.");
          }
          if (updatedCaseTransfer.status === "expired") {
            this.cleanUp();
            this.showWarningAlert("Case Transfer", "The case transfer expired.");
          }
        }
      }
    },
    subscribeChannel() {
      if (this.caseTransfer?.pubnubChannel && this.pubNubSub?.api?.subscribe) {
        const pubNubInstance = new PubNub({
          subscribeKey: this.pubNubSub.api.subscribe,
          uuid: this.twiageCase.id,
        });

        pubNubInstance.subscribe({
          channels: [this.caseTransfer.pubnubChannel],
        });

        pubNubInstance.addListener({
          message: (m) => {
            if (m && m.message) {
              if (m.message.type === "CASE_TRANSFER_UPDATED") {
                if (this.caseTransfer?._id) {
                  this.state(this.caseTransfer._id);
                }
              }
            }
          },
        });

        this.pubNubInstance = pubNubInstance;
      }
    },
    unSubscribeChannel() {
      if (this.pubNubInstance && this.caseTransfer.pubnubChannel) {
        setTimeout(
          () =>
            this.pubNubInstance.unsubscribe({
              channels: [this.caseTransfer.pubnubChannel],
            }),
          2000,
        );
      }
    },
    startInternetStatusListener() {
      this.isListening = true;
      this.runInternetStatusListener();
    },
    runInternetStatusListener() {
      if (this.isListening) {
        setTimeout(async () => {
          const isOnline = await this.checkOnlineStatus();
          if (isOnline) {
            if (!this.isOnline) {
              if (this.caseTransfer && this.caseTransfer._id) {
                this.subscribeChannel();
                this.state(this.caseTransfer._id);
              }
              this.stopCaseTransferTimeout();
            }
          } else {
            this.startCaseTransferTimeout();
          }

          this.isOnline = isOnline;
          this.runInternetStatusListener();
        }, 5000);
      }
    },
    stopInternetStatusListener() {
      this.isListening = false;
    },
    startCaseTransferTimeout() {
      if (!this.timeout) {
        this.timeout = setTimeout(() => {
          this.cleanUp();
          this.showWarningAlert("Case Transfer", "The case transfer timed out and it was cancelled.");
        }, 60000);
      }
    },
    stopCaseTransferTimeout() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = undefined;
      }
    },
    showTransferModal() {
      this.$refs["case-transfer-modal"].show();
    },
    hideTransferModal() {
      this.$refs["case-transfer-modal"].hide();
    },
    cleanUp() {
      this.unSubscribeChannel();
      this.stopInternetStatusListener();
      this.clear();
      this.hideTransferModal();
      this.$bvModal.hide("caseTransferRequestDialog");
    },
    showRequestAlert(message) {
      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Case Transfer Request",
          size: "sm",
          buttonSize: "sm",
          okVariant: "success",
          okTitle: "Accept",
          cancelTitle: "Decline",
          footerClass: "p-2",
          hideHeaderClose: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
          centered: true,
          id: "caseTransferRequestDialog",
        })
        .then((accepted) => {
          if (accepted) {
            this.onCaseTransferAccept();
          } else if (accepted === false) {
            this.onCaseTransferDecline();
          }
        })
        .catch(() => {
          // console.log("logout error", error);
        });
    },
    showWarningAlert(title, message, callback) {
      showOkDialog(this.$bvModal, title, message, callback);
    },
    canCancelTransfer() {
      return (
        this.caseTransfer &&
        this.caseTransfer.status === "open" &&
        (!this.caseTransfer.request ||
          this.caseTransfer.request.status === "denied" ||
          this.caseTransfer.request.status === "done" ||
          this.caseTransfer.request.status === "cancelled")
      );
    },
  },
};
</script>

<style scoped>
.media-button {
  position: relative;
  background-color: white;
  color: black;
  min-height: 64px;
}

.media-button span.media-button-message {
  position: absolute;
  font-size: 12px;
  font-weight: normal;
  margin: 0;
  left: 52px;
  top: 26px;
}
</style>
